/** 手机触控：独立操作按钮使用至少44px点击区域；桌面保持原紧凑布局，业务权限、提交与跳转逻辑不变。 */
import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { toast } from "../ui/sonner";
import { Upload, FileCheck2, Loader2, Sparkles, AlertCircle, RefreshCw, Wand2 } from "lucide-react";
import { newCollectionRequestId } from "./CollectionFollowupStatus";
import FinanceDocumentScanner from "../finance/FinanceDocumentScanner";
import { appendScannedOriginals } from "../../lib/documentScanner";
import { reviewedPaymentFields } from "./paymentScanFields";

const inp = "mt-1 w-full text-sm rounded-md border border-slate-200 px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-300";

/* 代客户上传支付凭证（合同期 / 变更单同一弹窗）：图片 / PDF + 收款人 / 付款人 / 金额 / 备注 → 财务待核销队列 + 收款沟通记录。

   已签约客户详情的专属需求：收款人默认当前账号姓名，付款人可选择 OWNER 或手填其他姓名，并登记 Check / Zelle / Wire / Cash。
   这些字段只在 CRM 已签约客户入口显示并写入凭证记录，项目财务等复用入口维持原表单，避免改变其他流程的数据口径。

   2026-10-13 新增「选文件后自动识别」（金额 / 支票号 / 付款人 / 日期 / 银行 → 回填输入框），并按用户口径补齐三点：
   ① **模式可选**：文件区上方给「AI 解析 / 直接上传」二选一（默认 AI 解析）。用户明确要求「不要都走一条路」——
      凭证清晰 / 已知道金额时可直接上传，不强制等识别。
   ② **识别中全屏锁死**：识别期间铺一层全屏遮罩（z-[9999]，盖过弹窗右上角关闭按钮的 z-[60]）并拦截
      ESC / 点遮罩 / 关闭按钮 / 底部取消 / 提交，避免用户在识别中途离开导致表单与结果错位；
      同时给一个「取消识别」按钮（用户口径：锁死但可取消），取消走 AbortController 真正中断请求。
   ③ 识别结果先供用户核对，仅经确认填入空白字段；预填金额、手填值和历史收款确认均不得被识别结果覆盖。
   识别失败 / 取消都**不影响上传**：只提示一行，用户照旧手动填写或直接上传（识别是便利功能，不是前置依赖）。
   识别用的模型走 AI 配置中心的独立用途 `proof`（系统管理 → AI 配置 → 模型映射可单独换）。 */
export function ProofUploadDialog({ api, url, title, amount, zh, onClose, onDone, historicalReceipt = false, archiveOnly = false, crmSigned = false, accountName = "" }) {
  const [file, setFile] = useState(null);
  const [scanFiles, setScanFiles] = useState([]);
  const [documentWarnings, setDocumentWarnings] = useState([]);
  const [f, setF] = useState({ payee: crmSigned ? accountName : "", payerName: "", paymentMethod: "", amount: amount != null ? String(amount) : "", note: "" });
  // 付款人需求没有指定默认 OWNER，因此保留未选择状态；选“其他”后才显示自由输入，避免误把默认值写入凭证。
  const [payerChoice, setPayerChoice] = useState("");
  // 历史补录是财务已到账确认，不能与普通上传混用；日期不默认今天，避免把旧款记入当期。
  const [paymentDate, setPaymentDate] = useState("");
  const [receivedConfirmed, setReceivedConfirmed] = useState(false);
  const [validation, setValidation] = useState("");
  const requestId = useRef(newCollectionRequestId());
  const submitting = useRef(false);
  const dateTouched = useRef(false);
  const recordHistory = historicalReceipt && !archiveOnly;
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("ai");   // AI 识别先核对再填空；直接上传只保存文件。
  // 识别状态：idle 未识别 / loading 识别中（全屏锁死） / done 已识别 / fail 未识别
  const [rec, setRec] = useState({ state: "idle", msg: "" });
  const touched = useRef({ payee: false, amount: false, payerName: false, paymentMethod: false, note: false });   // 用户手动改过的字段（识别不得覆盖，包括用户清空默认收款人）
  const recToken = useRef(0);        // 识别请求令牌：换文件 / 取消后旧响应作废，避免结果错位回填
  const abortRef = useRef(null);     // 「取消识别」用的中断器
  const loading = rec.state === "loading";
  const edit = (k, v) => { touched.current[k] = true; setF((prev) => ({ ...prev, [k]: v })); };
  const fillEmpty = (fields) => {
    const note = fields.note || [fields.checkNo ? `Check #${fields.checkNo}` : "", fields.bank, fields.payDate].filter(Boolean).join(" · ");
    const recognizedMethod = String(fields.paymentMethod || fields.method || "").trim().toLowerCase();
    const recognizedPayer = String(fields.payerName || "").trim();
    const payerName = crmSigned && recognizedPayer.toUpperCase() === "OWNER" ? "OWNER" : fields.payerName;
    const candidates = { payee: fields.payee, amount: fields.amount, payerName, paymentMethod: ["check", "zelle", "wire", "cash"].includes(recognizedMethod) ? recognizedMethod : "", note };
    if (crmSigned && !String(f.payerName || "").trim() && !touched.current.payerName && recognizedPayer) {
      setPayerChoice(recognizedPayer.toUpperCase() === "OWNER" ? "owner" : "other");
    }
    setF(prev => {
      const next = { ...prev };
      Object.entries(candidates).forEach(([key, value]) => {
        if (!String(prev[key] ?? "").trim() && !touched.current[key] && value != null) next[key] = String(value);
      });
      return next;
    });
    const date = fields.payDate || fields.date;
    if (recordHistory && !dateTouched.current && /^\d{4}-\d{2}-\d{2}$/.test(date || "")) setPaymentDate(prev => prev || date);
  };

  // 识别中「不许离开」的最后一道闸：本页是「列表 + 详情抽屉」模板，CrmDetailDrawer 在 window 冒泡阶段
  // 监听 ESC 关抽屉（→ 路由跳回 /crm/signed），点遮罩 / 关按钮虽被全屏遮罩挡住，但键盘仍能触达。
  // 这里用 **capture 阶段** 监听并在识别中吞掉 ESC：capture 先于冒泡，抽屉的监听收不到，页面不会被带离。
  useEffect(() => {
    if (!loading) return;
    const blockEsc = (e) => { if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); } };
    window.addEventListener("keydown", blockEsc, true);
    return () => window.removeEventListener("keydown", blockEsc, true);
  }, [loading]);

  const runRecognize = async (target) => {
    if (!target) return;
    const token = ++recToken.current;
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setRec({ state: "loading", msg: zh ? "正在识别凭证…" : "Reading the proof…" });
    try {
      const fd = new FormData();
      fd.append("file", target);
      const { data } = await api.post("/crm/signed/proof/recognize", fd, { headers: { "Content-Type": "multipart/form-data" }, signal: ctrl.signal });
      if (token !== recToken.current) return;   // 期间又换了文件 / 被取消 → 丢弃本次结果
      if (!data?.ok) { setRec({ state: "fail", msg: data?.error || (zh ? "未能识别，请手动填写" : "Could not read — please fill in manually") }); return; }
      setRec({
        state: "done", fields: data.fields || {}, warnings: data.warnings || [], uncertainFields: data.uncertainFields || [],
        msg: zh ? "识别完成，请核对结果后填入空白字段。" : "Review the recognized details before filling empty fields.",
      });
    } catch (e) {
      if (token !== recToken.current) return;   // 已被取消（cancelRecognize 已把状态复位）→ 不覆盖提示
      if (e?.code === "ERR_CANCELED" || e?.name === "CanceledError" || e?.name === "AbortError") return;
      setRec({ state: "fail", msg: e?.detailText || (zh ? "识别失败，请手动填写" : "Recognition failed — please fill in manually") });
    } finally {
      if (abortRef.current === ctrl) abortRef.current = null;
    }
  };

  // 取消识别：作废本次结果 + 真正中断请求，解锁弹窗（用户口径：锁死但可取消）
  const cancelRecognize = () => {
    recToken.current += 1;
    try { abortRef.current?.abort(); } catch { /* 忽略 */ }
    abortRef.current = null;
    setRec({ state: "idle", msg: "" });
  };

  const pick = (list) => {
    const next = Array.from(list || []);
    if (next.length) setScanFiles(next);
  };
  const prepared = ({ scanFile, originals, recognition }) => {
    if (!scanFile && originals.length !== 1) { toast.error(zh ? "请将多张照片合成为一份 PDF。" : "Combine the photos into one PDF."); return; }
    setFile(scanFile || originals[0]); setScanFiles([]);
    setDocumentWarnings(recognition?.warnings || []);
    if (mode === "ai" && recognition?.applyFields) fillEmpty(reviewedPaymentFields(recognition));
    setRec({ state: "idle", msg: "" });
  };

  // 切换模式：识别中不允许切（遮罩已盖住按钮，这里是兜底）；切回 AI 且还没识别过 → 立刻补一次识别
  const switchMode = (m) => {
    if (loading || busy || m === mode) return;
    setMode(m);
    if (m === "ai" && file && rec.state !== "done") runRecognize(file);
    if (m === "direct") setRec({ state: "idle", msg: "" });
  };

  const submit = async () => {
    if (submitting.current || busy || loading) return;
    if (!file) { toast.error(zh ? "请选择凭证文件（图片 / PDF）" : "Choose an image or PDF"); return; }
    if (recordHistory && (!paymentDate || !receivedConfirmed || !Number.isFinite(Number(f.amount)) || Number(f.amount) <= 0)) {
      setValidation(zh ? "请填写实际收款日期、正数到账金额，并确认款项已到账。" : "Enter the receipt date and a positive amount, then confirm the funds were received.");
      return;
    }
    setValidation("");
    submitting.current = true;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("payee", f.payee); fd.append("payerName", f.payerName); fd.append("amount", f.amount); fd.append("note", f.note);
      if (crmSigned) fd.append("paymentMethod", f.paymentMethod || "");
      appendScannedOriginals(fd, file);
      // 网络重试沿用本次操作编号；金额由服务端按实际到账累加，禁止重复记账或发送客户回执。
      if (recordHistory) {
        fd.append("historicalReceipt", "true"); fd.append("paymentDate", paymentDate); fd.append("requestId", requestId.current);
      }
      const { data } = await api.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });
      // 已收款补传只留档；通知异常时凭证已保存，不让用户误以为失败而重复上传。
      if (data.archived) toast.success(zh ? "已补传凭证留档，收款金额不变" : "Proof archived; recorded payment unchanged");
      else if (data.historicalReceipt) toast.success(data.payStatus === "paid"
        ? (zh ? "已补录凭证并完成收款，未发送客户通知" : "Historical payment recorded in full; no customer notification sent")
        : (zh ? "已补录本次到账，剩余款项仍待收，未发送客户通知" : "Historical receipt recorded; a balance remains. No customer notification sent"));
      else if (data.notificationWarning) toast.warning(zh ? "凭证已保存并进入待核销队列；通知发送失败，请提醒财务查看" : "Proof saved for review; notification failed. Please ask finance to check the queue.");
      else toast.success(zh ? `凭证已上传，已通知财务 ${data.notified} 人核销` : `Proof uploaded · finance notified (${data.notified})`);
      onDone?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { submitting.current = false; setBusy(false); }
  };

  const seg = (active) => `px-2.5 py-1 rounded-md transition-colors ${active ? "bg-white text-brand-700 shadow-sm" : "text-slate-500 hover:text-slate-700"}`;
  return (
    <Dialog open onOpenChange={(o) => { if (!o && !loading && !busy && !scanFiles.length) onClose(); }}>
      <DialogContent
        className="max-w-md"
        data-testid="proof-upload-dialog"
        closeDisabled={busy || loading || !!scanFiles.length}
        // 识别中锁死：ESC / 点弹窗外 / 关闭按钮 一律拦截（右上角关闭按钮走的是 onOpenChange，上面也拦了一层）
        onEscapeKeyDown={(e) => { if (loading || busy) e.preventDefault(); }}
        onInteractOutside={(e) => { if (loading || busy) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (loading || busy) e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base"><Upload className="h-4 w-4 shrink-0 text-brand-500" />{recordHistory ? (zh ? "补录历史已收款" : "Record historical payment") : archiveOnly ? (zh ? "补传收款凭证" : "Archive payment proof") : (zh ? "上传支付凭证" : "Upload payment proof")} · {title}</DialogTitle>
          <DialogDescription>{recordHistory
            ? (zh ? "用于补充已到账的设计变更款。上传凭证后直接登记本次实收；足额后显示收款完成，不发送客户催款或收款回执。" : "For change-order payments already received. Upload proof and record this receipt; full payment closes the balance. No reminder or receipt is sent to the customer.")
            : archiveOnly ? (zh ? "该笔已收款，附件仅作留档，已登记金额与收款状态保持不变。" : "This payment is settled. The proof is archived without changing recorded amounts or payment status.")
            : (zh ? "客户通过微信 / 邮件发来的支票照片、转账截图或回单：上传后进入财务待核销队列，财务核对到账后标记已收并发送客户回执。" : "Check photo, transfer screenshot or bank slip from the customer. Finance verifies it, marks the period paid and sends the receipt.")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {/* 模式二选一（默认 AI 解析）：让用户自己决定要不要走识别，而不是所有凭证都强制过一遍 AI */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-slate-500">{zh ? "处理方式" : "Mode"}</span>
            <div className="inline-flex items-center rounded-lg border border-slate-200 bg-slate-50 p-0.5 text-xs font-semibold" data-testid="proof-upload-mode" data-mode={mode}>
              <button type="button" disabled={loading || busy} onClick={() => switchMode("ai")} data-testid="proof-mode-ai" className={"min-h-11 min-w-11 md:min-h-0 md:min-w-0 " + (seg(mode === "ai"))}><Wand2 className="h-3 w-3 inline mr-1" />{zh ? "AI 解析" : "AI parse"}</button>
              <button type="button" disabled={loading || busy} onClick={() => switchMode("direct")} data-testid="proof-mode-direct" className={"min-h-11 min-w-11 md:min-h-0 md:min-w-0 " + (seg(mode === "direct"))}><Upload className="h-3 w-3 inline mr-1" />{zh ? "直接上传" : "Direct upload"}</button>
            </div>
            <span className="text-[11px] text-slate-400">{mode === "ai" ? (zh ? "扫描后核对识别结果，只填入空白字段" : "Review scanned details; fill empty fields only") : (zh ? "扫描或保留原件，按你填的内容上传" : "Scan or keep originals; upload as entered")}</span>
          </div>
          <label className="block">
            <span className="text-xs text-slate-500">{zh ? "凭证文件（图片 / PDF，≤20MB）" : "File (image / PDF, ≤20MB)"}</span>
            <input type="file" multiple accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.gif,image/*,application/pdf" onChange={(e) => { pick(e.target.files); e.target.value = ""; }} disabled={loading || busy} className="mt-1 block w-full text-xs text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-brand-50 file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-brand-700 hover:file:bg-brand-100" data-testid="proof-upload-file" />
            {file && <span className="mt-1 inline-flex items-center gap-1 text-[11px] text-emerald-700" data-testid="proof-upload-filename"><FileCheck2 className="h-3 w-3" />{file.name} · {(file.size / 1024).toFixed(0)} KB</span>}
            {file?.documentScan && <span className="mt-1 block text-xs text-slate-500">{zh ? "扫描件及原件：" : "Scan and originals: "}{file.documentScan.originals.map(f => f.name).join("、")}</span>}
          </label>
          {documentWarnings.some(warning => ["invoice_date_not_payment_date", "invoice_not_proof_of_payment"].includes(warning)) && <p className="text-xs text-amber-700" role="status" data-testid="proof-invoice-warning">{zh ? "账单金额及开票日期不能证明实际收款。请独立核对到账金额和实际收款日期。" : "An invoice amount or invoice date does not establish payment. Independently verify the amount and date received."}</p>}
          {/* 识别状态行：识别中 / 已识别并回填 / 未能识别（均不阻断上传） */}
          {rec.state !== "idle" && (
            <div className="flex items-center gap-2 flex-wrap">
              <p className={`text-[11px] inline-flex items-center gap-1 ${rec.state === "fail" ? "text-amber-700" : rec.state === "done" ? "text-emerald-700" : "text-slate-500"}`} data-testid="proof-recognize-status" data-state={rec.state}>
                {rec.state === "loading" ? <Loader2 className="h-3 w-3 animate-spin" /> : rec.state === "done" ? <Sparkles className="h-3 w-3" /> : <AlertCircle className="h-3 w-3" />}
                {rec.msg}
              </p>
              {rec.fields && <div className="w-full space-y-1 text-xs text-slate-600">
                <p className="break-words">{Object.entries(rec.fields).filter(([, value]) => value != null && value !== "").map(([key, value]) => `${key}: ${value}`).join(" · ")}</p>
                <Button type="button" variant="outline" size="sm" onClick={() => { fillEmpty(reviewedPaymentFields({ ...rec, applyFields: true })); setDocumentWarnings(rec.warnings || []); setRec(prev => ({ ...prev, fields: null, msg: zh ? "已填入空白字段，已有内容保留。" : "Empty fields filled; existing values retained." })); }} data-testid="proof-recognize-apply">{zh ? "已核对，填入空白字段" : "Reviewed — fill empty fields"}</Button>
              </div>}
              {rec.state !== "loading" && file && (
                <button type="button" disabled={busy} onClick={() => runRecognize(file)} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-[11px] text-slate-500 hover:text-brand-600 inline-flex items-center gap-0.5" data-testid="proof-recognize-retry">
                  <RefreshCw className="h-3 w-3" />{zh ? "重新识别" : "Retry"}
                </button>
              )}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><span className="text-xs text-slate-500">{crmSigned ? (zh ? "收款人" : "Recipient") : (zh ? "公司 / 收款方" : "Company / payee")}</span><input className={inp} value={f.payee} disabled={loading || busy} onChange={(e) => edit("payee", e.target.value)} data-testid="proof-upload-payee" /></label>
            <label className="block"><span className="text-xs text-slate-500">{recordHistory ? (zh ? "本次实收金额 $" : "Amount received $") : (zh ? "付款金额 $" : "Amount $")}</span><input type="number" min={recordHistory ? "0.01" : "0"} step="0.01" className={inp} value={f.amount} disabled={loading || busy} onChange={(e) => edit("amount", e.target.value)} data-testid="proof-upload-amount" /></label>
            {crmSigned ? <div className="block">
              <label><span className="text-xs text-slate-500">{zh ? "付款人" : "Payer"}</span>
                <select className={inp} value={payerChoice} disabled={loading || busy} onChange={(e) => {
                  const choice = e.target.value;
                  setPayerChoice(choice);
                  touched.current.payerName = true;
                  setF((prev) => ({ ...prev, payerName: choice === "owner" ? "OWNER" : "" }));
                }} data-testid="proof-upload-payer-choice">
                  <option value="">{zh ? "请选择" : "Select"}</option>
                  <option value="owner">OWNER</option>
                  <option value="other">{zh ? "其他（手动输入）" : "Other (enter manually)"}</option>
                </select>
              </label>
              {payerChoice === "other" && <input className={inp} value={f.payerName} disabled={loading || busy} onChange={(e) => edit("payerName", e.target.value)} placeholder={zh ? "请输入付款人姓名" : "Enter payer name"} aria-label={zh ? "手动输入付款人" : "Enter payer name"} data-testid="proof-upload-payer" />}
            </div> : <label className="block"><span className="text-xs text-slate-500">{zh ? "付款人" : "Payer"}</span><input className={inp} value={f.payerName} disabled={loading || busy} onChange={(e) => edit("payerName", e.target.value)} data-testid="proof-upload-payer" /></label>}
            {crmSigned && <label className="block"><span className="text-xs text-slate-500">{zh ? "支付方式" : "Payment method"}</span>
              <select className={inp} value={f.paymentMethod} disabled={loading || busy} onChange={(e) => edit("paymentMethod", e.target.value)} data-testid="proof-upload-payment-method">
                <option value="">{zh ? "请选择" : "Select"}</option>
                <option value="check">Check</option><option value="zelle">Zelle</option><option value="wire">Wire</option><option value="cash">Cash</option>
              </select>
            </label>}
          </div>
          <label className="block"><span className="text-xs text-slate-500">{zh ? "备注（支票号 / 转账渠道 / 日期）" : "Note (check no. / channel / date)"}</span><input className={inp} value={f.note} disabled={loading || busy} onChange={(e) => edit("note", e.target.value)} data-testid="proof-upload-note" /></label>
          {recordHistory && <div className="space-y-3 rounded-lg border border-brand-100 bg-brand-50/50 p-3" data-testid="proof-historical-fields">
            <label className="block"><span className="text-xs text-slate-600">{zh ? "实际收款日期 *" : "Date received *"}</span><input type="date" required className={inp} value={paymentDate} disabled={loading || busy} onChange={(e) => { dateTouched.current = true; setPaymentDate(e.target.value); }} data-testid="proof-payment-date" /></label>
            <label className="flex min-h-11 items-start gap-2 text-xs leading-5 text-slate-700"><input type="checkbox" className="mt-1 accent-brand-600" checked={receivedConfirmed} disabled={loading || busy} onChange={(e) => setReceivedConfirmed(e.target.checked)} data-testid="proof-received-confirm" /><span>{zh ? "我已核对款项到账，本次为历史收款补录，不向客户发送通知。" : "I verified these funds were received. This is a historical entry; do not notify the customer."}</span></label>
          </div>}
          {validation && <p role="alert" className="text-xs text-destructive" data-testid="proof-upload-validation">{validation}</p>}
          <div className="flex-wrap md:flex-nowrap flex justify-end gap-2 pr-16 sm:pr-0">
            <Button variant="outline" size="sm" disabled={loading || busy} onClick={onClose} data-testid="proof-upload-cancel">{zh ? "取消" : "Cancel"}</Button>
            <Button size="sm" disabled={busy || loading || !file} onClick={submit} className="bg-brand-600 hover:bg-brand-700 text-white" data-testid="proof-upload-submit"><Upload className="h-3.5 w-3.5 mr-1" />{busy ? (zh ? "保存中…" : "Saving…") : recordHistory ? (zh ? "上传并确认收款" : "Upload & record receipt") : archiveOnly ? (zh ? "上传留档" : "Upload to archive") : (zh ? "上传并通知财务" : "Upload & notify finance")}</Button>
          </div>
        </div>
      </DialogContent>

      {/* 识别中全屏遮罩：盖过弹窗与右上角关闭按钮（z-[60]），挡住一切交互；仅保留「取消识别」出口 */}
      {loading && (
        <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center gap-3 bg-slate-900/55 backdrop-blur-sm" data-testid="proof-recognize-overlay">
          <Loader2 className="h-9 w-9 animate-spin text-white" />
          <p className="text-sm font-semibold text-white">{zh ? "AI 正在识别凭证…" : "Reading the proof…"}</p>
          <p className="text-[11px] text-white/70">{zh ? "识别完成前请留在本页（通常 3~10 秒）" : "Please stay on this page (usually 3–10s)"}</p>
          <Button variant="outline" size="sm" onClick={cancelRecognize} data-testid="proof-recognize-cancel" className="mt-1">{zh ? "取消识别" : "Cancel reading"}</Button>
        </div>
      )}
      <FinanceDocumentScanner api={api} zh={zh} files={scanFiles} maxOriginalFiles={1} maxBytes={20 * 1024 * 1024} onClose={() => setScanFiles([])} onPrepared={prepared} />
    </Dialog>
  );
}
